import React, { useState } from 'react';
import TrendChart from './TrendChart';
import '../styles/AllTrendsPage.css';

interface TrendData {
  date: string;
  value: number;
}

interface IndicatorInfo {
  id: string;
  name: string;
  unit: string;
  data: TrendData[];
  color: string;
  targetValue?: number;
  targetLabel?: string;
  status: 'normal' | 'caution' | 'abnormal';
  description: string;
}

interface AllTrendsPageProps {
  onBack?: () => void;
}

const AllTrendsPage: React.FC<AllTrendsPageProps> = ({ onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedIndicator, setSelectedIndicator] = useState<IndicatorInfo | null>(null);

  const indicators: IndicatorInfo[] = [
    {
      id: 'blood-lipid',
      name: '血脂水平',
      unit: 'mmol/L',
      data: [
        { date: '2024年1月', value: 5.2 },
        { date: '2024年2月', value: 5.8 },
        { date: '2024年3月', value: 6.1 },
        { date: '2024年4月', value: 5.9 },
        { date: '2024年5月', value: 5.6 },
        { date: '2024年6月', value: 5.4 }
      ],
      color: '#FF6B6B',
      targetValue: 5.2,
      targetLabel: '正常上限',
      status: 'caution',
      description: '总胆固醇偏高，建议控制饮食和适量运动'
    },
    {
      id: 'lung-nodule',
      name: '肺结节大小',
      unit: 'mm',
      data: [
        { date: '2024年1月', value: 3.2 },
        { date: '2024年2月', value: 3.1 },
        { date: '2024年3月', value: 3.3 },
        { date: '2024年4月', value: 3.2 },
        { date: '2024年5月', value: 3.0 },
        { date: '2024年6月', value: 2.9 }
      ],
      color: '#4ECDC4',
      targetValue: 5.0,
      targetLabel: '关注线',
      status: 'normal',
      description: '肺结节呈缩小趋势，继续观察'
    },
    {
      id: 'blood-pressure-systolic',
      name: '收缩压',
      unit: 'mmHg',
      data: [
        { date: '2024年1月', value: 135 },
        { date: '2024年2月', value: 138 },
        { date: '2024年3月', value: 142 },
        { date: '2024年4月', value: 140 },
        { date: '2024年5月', value: 136 },
        { date: '2024年6月', value: 134 }
      ],
      color: '#FF9F43',
      targetValue: 140,
      targetLabel: '高血压界限',
      status: 'caution',
      description: '血压偏高，需要控制盐分摄入'
    },
    {
      id: 'blood-sugar',
      name: '空腹血糖',
      unit: 'mmol/L',
      data: [
        { date: '2024年1月', value: 5.8 },
        { date: '2024年2月', value: 6.1 },
        { date: '2024年3月', value: 5.9 },
        { date: '2024年4月', value: 5.7 },
        { date: '2024年5月', value: 5.6 },
        { date: '2024年6月', value: 5.5 }
      ],
      color: '#A55EEA',
      targetValue: 6.1,
      targetLabel: '正常上限',
      status: 'normal',
      description: '血糖控制良好，继续保持'
    },
    {
      id: 'bmi',
      name: 'BMI指数',
      unit: 'kg/m²',
      data: [
        { date: '2024年1月', value: 24.1 },
        { date: '2024年2月', value: 23.8 },
        { date: '2024年3月', value: 23.5 },
        { date: '2024年4月', value: 23.2 },
        { date: '2024年5月', value: 23.0 },
        { date: '2024年6月', value: 22.9 }
      ],
      color: '#26DE81',
      targetValue: 24.0,
      targetLabel: '正常上限',
      status: 'normal',
      description: 'BMI逐步下降至正常范围'
    },
    {
      id: 'heart-rate',
      name: '静息心率',
      unit: '次/分',
      data: [
        { date: '2024年1月', value: 78 },
        { date: '2024年2月', value: 76 },
        { date: '2024年3月', value: 74 },
        { date: '2024年4月', value: 72 },
        { date: '2024年5月', value: 73 },
        { date: '2024年6月', value: 71 }
      ],
      color: '#FC5185',
      targetValue: 100,
      targetLabel: '正常上限',
      status: 'normal',
      description: '心率正常且稳定'
    }
  ];

  const categories = [
    { id: 'all', name: '全部指标', count: indicators.length },
    { id: 'abnormal', name: '异常指标', count: indicators.filter(i => i.status === 'abnormal').length },
    { id: 'caution', name: '需关注', count: indicators.filter(i => i.status === 'caution').length },
    { id: 'normal', name: '正常指标', count: indicators.filter(i => i.status === 'normal').length }
  ];

  const getFilteredIndicators = () => {
    if (selectedCategory === 'all') return indicators;
    return indicators.filter(indicator => indicator.status === selectedCategory);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return '#34c759';
      case 'caution': return '#ff9500';
      case 'abnormal': return '#ff3b30';
      default: return '#8e8e93';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'normal': return '正常';
      case 'caution': return '需关注';
      case 'abnormal': return '异常';
      default: return '';
    }
  };

  const handleViewDetail = (indicator: IndicatorInfo) => {
    setSelectedIndicator(indicator);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedIndicator(null);
  };

  return (
    <div className="all-trends-page">
      <div className="page-header">
        <div className="header-top">
          <button className="back-btn" onClick={onBack}>
            <span className="back-icon">←</span>
            返回
          </button>
          <h1 className="page-title">全部指标趋势</h1>
          <div className="header-spacer"></div>
        </div>
      </div>

      <div className="category-filters">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            <span className="category-name">{category.name}</span>
            <span className="category-count">({category.count})</span>
          </button>
        ))}
      </div>

      <div className="trends-grid">
        {getFilteredIndicators().map((indicator) => (
          <div key={indicator.id} className="trend-card">
            <div className="trend-card-header">
              <div className="title-with-unit">
                <h3 className="indicator-name">{indicator.name}</h3>
                <span className="indicator-unit">({indicator.unit})</span>
              </div>
              {indicator.targetValue && (
                <div className="target-info-inline">
                  <span className="target-label">{indicator.targetLabel}: {indicator.targetValue}{indicator.unit}</span>
                </div>
              )}
            </div>

            <div className="trend-chart-container">
              <TrendChart
                data={indicator.data}
                color={indicator.color}
                unit={` ${indicator.unit}`}
                thresholdValue={indicator.targetValue}
                thresholdLabel={indicator.targetLabel}
              />
            </div>

            <div className="trend-description">
              <p>{indicator.description}</p>
            </div>

            <div className="trend-actions">
              <button 
                className="detail-btn"
                onClick={() => handleViewDetail(indicator)}
              >
                查看详情
              </button>
            </div>
          </div>
        ))}
      </div>

      {getFilteredIndicators().length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>暂无此类型指标</h3>
          <p>选择其他类别查看相关指标趋势</p>
        </div>
      )}

      <div className="page-footer">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-value">{indicators.length}</span>
            <span className="stat-label">总指标数</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{indicators.filter(i => i.status === 'normal').length}</span>
            <span className="stat-label">正常指标</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{indicators.filter(i => i.status === 'caution').length}</span>
            <span className="stat-label">需关注</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{indicators.filter(i => i.status === 'abnormal').length}</span>
            <span className="stat-label">异常指标</span>
          </div>
        </div>
      </div>

      {/* 详情模态框 */}
      {showDetailModal && selectedIndicator && (
        <div className="detail-modal-overlay" onClick={closeDetailModal}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>{selectedIndicator.name}</h3>
              <button className="detail-modal-close" onClick={closeDetailModal}>
                ✕
              </button>
            </div>
            <div className="detail-modal-content">
              <div className="detail-info-section">
                <h4>指标说明</h4>
                <p>{selectedIndicator.description}</p>
              </div>
              <div className="detail-info-section">
                <h4>历史数据</h4>
                <div className="detail-data-list">
                  {selectedIndicator.data.map((item, index) => (
                    <div key={index} className="detail-data-item">
                      <span className="detail-date">{item.date}</span>
                      <span className="detail-value">{item.value}{selectedIndicator.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
              {selectedIndicator.targetValue && (
                <div className="detail-info-section">
                  <h4>参考范围</h4>
                  <p>{selectedIndicator.targetLabel}: {selectedIndicator.targetValue}{selectedIndicator.unit}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTrendsPage; 